import dayjs, { Dayjs } from 'dayjs';
import type { IConfigurationVariable, IValueType } from '../../types';

type IRangeType = string | number | undefined;
type IExtendedValueType = IValueType | Dayjs;

export const getNumericValidationInfo = (
  variable: IConfigurationVariable,
  value: number,
  isOptional: boolean = false
) => {
  const isEqual = (a: IExtendedValueType, b: IExtendedValueType) => a === b;
  const isInRange = (lower: IRangeType, upper: IRangeType, currentValue: IExtendedValueType) =>
    lower !== undefined &&
    upper !== undefined &&
    currentValue !== null &&
    currentValue !== undefined &&
    lower <= currentValue &&
    upper >= currentValue;

  return getValidationInfo(variable, value, isEqual, isInRange, (v) => v as string, isOptional);
};

export const getDateValidationInfo = (variable: IConfigurationVariable, value: Dayjs | null) => {
  const formatDate = (v: IValueType) => dayjs(v as string).format('MM/DD/YYYY');
  const isEqual = (a: IExtendedValueType, b: IExtendedValueType) =>
    dayjs(a as IRangeType).format() === dayjs(b as IRangeType).format();
  const isInRange = (lower: IRangeType, upper: IRangeType, currentValue: IExtendedValueType) =>
    (dayjs(lower).isBefore(currentValue as Dayjs) || isEqual(lower, currentValue)) &&
    (dayjs(upper).isAfter(currentValue as Dayjs) || isEqual(upper, currentValue));

  return getValidationInfo(variable, value, isEqual, isInRange, formatDate);
};

const getValidationInfo = (
  variable: IConfigurationVariable,
  value: IExtendedValueType,
  isEqual: (a: IExtendedValueType, b: IExtendedValueType) => boolean,
  isInRange: (lower: IRangeType, upper: IRangeType, currentValue: IExtendedValueType) => boolean,
  messageDelegate: (v: IValueType) => string,
  isOptional: boolean = false
) => {
  //assume that unset value is always valid
  let valid = value === undefined || value === null;
  const validValues: string[] = [];
  if (!variable?.values) {
    return { valid, validValues };
  }
  for (const variableValue of variable.values) {
    const isDiscrete = Object.hasOwn(variableValue, 'name');
    const entry = isDiscrete
      ? processDiscreteValue(variableValue, value, isEqual, messageDelegate, isOptional)
      : processRangeValue(variableValue, value, isInRange, messageDelegate, isOptional);
    if (entry) {
      if (entry.valid) valid = true;
      validValues.push(entry.display);
    }
  }

  return { valid, validValues };
};

// For optional numerics, 0 means "unassigned" and is excluded from validity and the hint.
const processDiscreteValue = (
  variableValue: IConfigurationVariable['values'][number],
  value: IExtendedValueType,
  isEqual: (a: IExtendedValueType, b: IExtendedValueType) => boolean,
  messageDelegate: (v: IValueType) => string,
  isOptional: boolean
): { valid: boolean; display: string } | null => {
  if (isOptional && Number(variableValue.value) === 0) return null;
  return {
    valid: isEqual(variableValue.value, value),
    display: `${messageDelegate(variableValue.value)}`,
  };
};

// For optional numerics, a range starting at 0 is treated as starting at 1.
const processRangeValue = (
  variableValue: IConfigurationVariable['values'][number],
  value: IExtendedValueType,
  isInRange: (lower: IRangeType, upper: IRangeType, currentValue: IExtendedValueType) => boolean,
  messageDelegate: (v: IValueType) => string,
  isOptional: boolean
): { valid: boolean; display: string } => {
  const effectiveLower = isOptional && Number(variableValue.lower) === 0 ? 1 : variableValue.lower;
  return {
    valid: isInRange(effectiveLower, variableValue.upper, value),
    display: `[${messageDelegate(effectiveLower)} - ${messageDelegate(variableValue.upper)}]`,
  };
};

export const getRangeDate = (variable: IConfigurationVariable, propertyName: string) => {
  if (!variable.values?.length) {
    return dayjs(new Date(0, 1, 1));
  }

  let value = null;
  if (propertyName === 'lower') {
    value = variable.values[0];
  } else if (propertyName === 'upper') {
    value = variable.values.at(-1);
  }

  if (!value) {
    throw new Error(`unknown propertyName: ${propertyName}`);
  }

  return value[propertyName] ? dayjs(value[propertyName]) : dayjs(value.value as string);
};

export const fixTimezoneOffset = (dayjsDate: Dayjs | null) => {
  if (!dayjsDate) {
    return null;
  }

  const copy = dayjs(dayjsDate);
  const date = dayjsDate.toDate();
  const offset = date.getTimezoneOffset();
  return copy.add(offset, 'minute').toDate();
};

export const getDateOnly = (dayjsDate: Dayjs | null) => {
  if (!dayjsDate) {
    return null;
  }

  if (!dayjsDate.isValid()) {
    return dayjsDate;
  }

  const dateOnlyString = dayjsDate.format('YYYY-MM-DDT00:00:00[Z]');
  return dayjs(dateOnlyString);
};

export const toInputFormat = (isoDateString: string) => {
  const datePart = isoDateString.split('T')[0];
  const dateParts = datePart.split('-');
  return `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
};
